import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  StyledAmenitiesCheckBoxItemDiv,
  StyledAmenitiesFilterDiv,
  StyledAmenitiesFilterHeaderContainerDiv,
  StyledAmenitiesTooltipDiv,
} from './index.styles';
import { StyledCheckboxItemDiv, StyledCheckboxItemsContainer } from '../../organisms/SearchResults/index.styles';
import { AmenitiesFiltersComponentsProps } from '../../organisms/SearchResults/index.types';
import { renderedData } from '../../organisms/SearchResults/searchResultsMainComponent/index';
import { SearchFilterContext } from '../../organisms/SearchResults/index.context';
import {
  amenityCodeToIconMap,
  SearchFacetsList,
  SearchFacetsListCodes,
  tooltipAmenities,
} from '../../organisms/SearchResults/index.constant';
import {
  SearchFacetsResponse,
  SearchFacetsResponseBuckets,
  SearchStateFilterObj,
} from '../../organisms/SearchResults/store/store.type';
import { useWindowSize } from '../../hooks';
import { BREAKPOINT_DESKTOP_1023, BREAKPOINT_TABLET } from '../../organisms/SearchResults/lib/application.constants';
import { formatNumberByLocale } from '../../utils';
import { isEmpty } from '@marriott/shared/mi-helper-utils';
import clsx from 'clsx';
import { useSessionUpdateContext } from '../../context/SessionUpdateContext';
import { usePageContext } from '../../context/PageContext/index';

declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface HTMLAttributes<T> {
    custom_click_track_value?: string;
  }
}

export const AmenitiesFilterComponent: React.FC<AmenitiesFiltersComponentsProps> = ({
  onItemSelect,
  allFilterPopUp,
  clearFilterState,
  jsonContents,
  itemList,
  tooltipRefElement,
  setTooltipContent,
  modalRefElement,
  setShowToolTip,
}) => {
  const clickTrackingLoc = 'Phoenix Search Results';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { jsonContent }: any = useContext(renderedData);
  const { currentLocale } = usePageContext();
  const jsonContentData = jsonContents ?? jsonContent;
  const { searchQueryState } = useContext(SearchFilterContext);
  const { interactionsSelected, setInteractionsSelected } = useSessionUpdateContext();
  const { width } = useWindowSize();
  const [selectedAmenityTooltip, setSelectedAmenityTooltip] = useState<string>('');

  const [currCheckedAmenitiesCode, setCurrCheckedAmenitiesCodeObj] = useState<SearchStateFilterObj | undefined>(
    searchQueryState[SearchFacetsList.amenities]
  );

  const labelRefs = useRef<HTMLLabelElement[]>([]);
  const [labelWidths, setLabelWidths] = useState<number[]>([]);

  const getCurrentFilterArray = (): SearchFacetsResponse => {
    // update amenties
    return itemList
      ? itemList
      : // eslint-disable-next-line @typescript-eslint/no-explicit-any
        searchQueryState?.filter?.filter((key: any) => key.type.code === SearchFacetsListCodes.amenities)[0];
  };

  const [currAvailableAmenities, setCurrAvailableAmenities] = useState<SearchFacetsResponse>(getCurrentFilterArray());

  const DEFAULT_SHOW_MORE_OPTION_AMENITIES_COUNT = 8;

  // in case available amenties are more than 8, show 8 else show all
  const [hideElementsAfter, setHideElementsAfter] = useState(
    allFilterPopUp && isEmpty(searchQueryState?.AMENITIES ?? {})
      ? DEFAULT_SHOW_MORE_OPTION_AMENITIES_COUNT
      : currAvailableAmenities?.buckets?.length
  );

  useEffect(() => {
    setCurrAvailableAmenities(getCurrentFilterArray());
  }, [searchQueryState.filter, itemList]);

  useEffect(() => {
    if (clearFilterState === null || clearFilterState === undefined) {
      setCurrCheckedAmenitiesCodeObj(undefined);
    }
  }, [clearFilterState]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSelectionChange = (event: any, amenity: SearchFacetsResponseBuckets): void => {
    if (event.key === 'Enter' || event.type === 'click' || event.keyCode === 13) {
      let obj: SearchStateFilterObj | undefined = { ...currCheckedAmenitiesCode };
      if (obj !== undefined) {
        if (amenity.code in obj) {
          delete obj[amenity.code];
        } else {
          obj[amenity.code] = amenity.code;
        }
      } else {
        obj = {};
        obj[amenity.code] = amenity.code;
      }
      setCurrCheckedAmenitiesCodeObj(obj);
      if (onItemSelect) {
        onItemSelect(SearchFacetsList.amenities, obj);
      }
    }
    setInteractionsSelected({ ...interactionsSelected, amenities: true });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleShowMoreLess = (event: any): void => {
    if (event.key === 'Enter' || event.type === 'click' || event.keyCode === 13) {
      setHideElementsAfter(
        hideElementsAfter < currAvailableAmenities.buckets?.length
          ? currAvailableAmenities.buckets?.length
          : DEFAULT_SHOW_MORE_OPTION_AMENITIES_COUNT
      );
    }
  };

  useEffect(() => {
    setCurrCheckedAmenitiesCodeObj(searchQueryState[SearchFacetsList.amenities]);
  }, [searchQueryState?.AMENITIES]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setToolTip = (event: any, amenity: SearchFacetsResponseBuckets) => {
    if (event.key === 'Enter' || event.keyCode === 13 || event.type === 'click') {
      const tooltipContent = {
        tooltipHeading: amenity?.label ?? '',
        tooltipCode: amenity?.code ?? '',
      };
      setTooltipContent(tooltipContent);
      setShowToolTip?.(true);
      setSelectedAmenityTooltip(amenity?.code as string);
    }
  };

  // Hide tooltip when focus moves away from the icon or tooltip
  const hideTooltip = () => {
    setShowToolTip?.(false);
    setSelectedAmenityTooltip('');
  };

  useEffect(() => {
    // After render, refs are attached
    const widths = labelRefs.current.map(ref => (ref ? ref.offsetWidth : 0));
    setLabelWidths(widths);
    hideTooltip();
  }, [currAvailableAmenities?.buckets, hideElementsAfter]); // Re-run if array changes

  useEffect(() => {
    const modalContent = modalRefElement?.current;
    if (modalContent) {
      modalContent.addEventListener('scroll', hideTooltip);
    }
  }, [modalRefElement]);

  return currAvailableAmenities?.type?.count ? (
    <StyledAmenitiesFilterDiv className="t-font-alt-m filter-container">
      {allFilterPopUp ? (
        <fieldset>
          <StyledAmenitiesFilterHeaderContainerDiv className="t-font-alt-m">
            <legend>
              <h4
                className={
                  (width && width > BREAKPOINT_DESKTOP_1023
                    ? `t-subtitle-l`
                    : width && width > BREAKPOINT_TABLET
                    ? `t-subtitle-l`
                    : `t-subtitle-m`) + ' mb-0 filter-heading'
                }
              >
                {jsonContentData?.amenitiesLabel}
              </h4>
            </legend>
            <h6 className="filter-sub-heading">{jsonContentData?.amenitiesSubLabel}</h6>
          </StyledAmenitiesFilterHeaderContainerDiv>
        </fieldset>
      ) : (
        ''
      )}
      <>
        <StyledCheckboxItemsContainer className="checkbox-container">
          {currAvailableAmenities?.buckets
            ?.filter((_amenity: SearchFacetsResponseBuckets, index: number) => index < hideElementsAfter)
            ?.map((amenity: SearchFacetsResponseBuckets, idx: number) => (
              <StyledCheckboxItemDiv
                className={`${
                  Object.keys(tooltipAmenities).includes(amenity?.code as string) ? 'tooltip-amenity' : ''
                } m-checkbox-selection-item item-container`}
                key={idx}
              >
                <StyledAmenitiesCheckBoxItemDiv zIndexValue={currAvailableAmenities.buckets?.length - idx}>
                  <input
                    onChange={(e): void => onSelectionChange(e, amenity)}
                    onClick={(e): void => onSelectionChange(e, amenity)}
                    className="checkbox-input"
                    type="checkbox"
                    checked={
                      currCheckedAmenitiesCode && amenity.code
                        ? amenity.code in currCheckedAmenitiesCode === true
                        : false
                    }
                    aria-checked={
                      currCheckedAmenitiesCode && amenity.code
                        ? amenity.code in currCheckedAmenitiesCode === true
                        : false
                    }
                    tabIndex={0}
                    onKeyDown={(e): void => onSelectionChange(e, amenity)}
                    id={`${amenity?.code}-checkbox`}
                    aria-labelledby={`${amenity?.code}`}
                  />
                  <label
                    id={`${amenity?.code}`}
                    htmlFor={`${amenity?.code}-checkbox`}
                    className="l-margin-top checkbox-label custom_click_track"
                    {...{
                      custom_click_track_value: `${clickTrackingLoc}| Amenity Selection: ${amenity?.label} |internal`,
                    }}
                    ref={el => {
                      if (el) {
                        labelRefs.current[idx] = el;
                      }
                    }}
                  >
                    <span
                      className={clsx(
                        'icon-s',
                        `icon-${amenityCodeToIconMap[amenity?.code] ?? amenity?.code ?? 'check'}`,
                        'item-icon'
                      )}
                    ></span>
                    <span className="t-font-s item-name">
                      {amenity?.label}{' '}
                      {amenity?.count && (
                        <span className="t-font-s ">({formatNumberByLocale(amenity?.count, currentLocale)})</span>
                      )}
                    </span>
                  </label>
                  {Object.keys(tooltipAmenities).includes(amenity?.code as string) && (
                    <StyledAmenitiesTooltipDiv labelWidth={labelWidths[idx] || 0}>
                      <span className="tooltip-information">
                        <span
                          className="m-link-icon-button-info tooltip-icon"
                          onClick={(e): void => setToolTip(e, amenity)}
                          onKeyDown={(e): void => setToolTip(e, amenity)}
                          onMouseEnter={() => {
                            const tooltipContent = {
                              tooltipHeading: amenity?.label ?? '',
                              tooltipCode: amenity?.code ?? '',
                            };
                            setTooltipContent(tooltipContent);
                            setShowToolTip?.(true);
                            setSelectedAmenityTooltip(amenity?.code as string);
                          }}
                          onMouseLeave={() => {
                            setShowToolTip?.(false);
                            setSelectedAmenityTooltip('');
                          }}
                          onBlur={() => hideTooltip()}
                          ref={selectedAmenityTooltip === amenity?.code ? tooltipRefElement : null}
                          aria-label={amenity?.label}
                          role="tooltip"
                          id={`amenities-filter-tooltip-${amenity?.code}`}
                          tabIndex={0}
                          aria-describedby="tooltip-dialog"
                        ></span>
                      </span>
                    </StyledAmenitiesTooltipDiv>
                  )}
                </StyledAmenitiesCheckBoxItemDiv>
              </StyledCheckboxItemDiv>
            ))}
        </StyledCheckboxItemsContainer>
        {allFilterPopUp && currAvailableAmenities.buckets?.length > DEFAULT_SHOW_MORE_OPTION_AMENITIES_COUNT ? (
          <div>
            <div
              className="t-label-alt-xs show-more-less-option custom_click_track"
              role="button"
              {...{
                custom_click_track_value: `${clickTrackingLoc}| ${
                  hideElementsAfter < currAvailableAmenities.buckets?.length
                    ? jsonContentData.showLessLabel
                    : jsonContentData.showMoreLabel
                } amenities options |internal`,
              }}
              onClick={(e): void => {
                handleShowMoreLess(e);
              }}
              onKeyDown={(e): void => {
                e.stopPropagation();
                handleShowMoreLess(e);
              }}
              tabIndex={0}
              aria-label={
                hideElementsAfter < currAvailableAmenities.buckets?.length
                  ? jsonContentData.showMoreLabel
                  : jsonContentData.showLessLabel
              }
            >
              {hideElementsAfter < currAvailableAmenities.buckets?.length
                ? jsonContentData.showMoreLabel
                : jsonContentData.showLessLabel}
              <div
                className={
                  hideElementsAfter < currAvailableAmenities.buckets?.length ? 'icon-arrow-down' : 'icon-arrow-up'
                }
              ></div>
            </div>
          </div>
        ) : (
          ''
        )}
      </>
      {allFilterPopUp ? <hr aria-hidden className="filter-separator" /> : ''}
    </StyledAmenitiesFilterDiv>
  ) : null;
};
